import { useEffect, useState } from "react";

import { Link, useParams } from "@tanstack/react-router";
import { ChevronLeft, CircleHelp, ClipboardList, Trophy, Users } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ColorDotStack } from "@/components/cinch/ColorDot";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { useWorkspace } from "../api";
import { BOARD_META, SWOT_BOARDS, type Board } from "../constants";
import { AIModelChip } from "../components/AIModelChip";
import { HowToTab } from "../components/HowToTab";
import { ImpactEffortBoard } from "../components/ImpactEffortBoard";
import { SessionSearch } from "../components/SessionSearch";
import { ParticipantsDialog } from "../components/ParticipantsDialog";
import { SummaryTab } from "../components/SummaryTab";
import { SwotBoardView } from "../components/SwotBoardView";
import { SwotSummaryTab } from "../components/SwotSummaryTab";

type TabKey = Board | "swot_summary" | "summary" | "how_to";

export function PlanningSessionPage() {
  const { sessionId } = useParams({ strict: false }) as { sessionId: string };
  const workspace = useWorkspace(sessionId);
  const [tab, setTab] = useState<TabKey>("strengths");
  const [participantsOpen, setParticipantsOpen] = useState(false);
  // The card/lane a search result pointed at: scrolled into frame, shaken,
  // and haloed until the user's next click.
  const [highlight, setHighlight] = useState<string | null>(null);

  useEffect(() => {
    if (!highlight) return;
    // Scroll the target into center-frame once it mounts (tab content renders
    // a tick after the switch; retry briefly).
    let tries = 0;
    const seek = window.setInterval(() => {
      const el = document.querySelector(`[data-planning-hl="${highlight}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
        window.clearInterval(seek);
      } else if (++tries > 20) {
        window.clearInterval(seek);
      }
    }, 100);
    // The next click anywhere dismisses the halo. Attach after a beat so the
    // click that selected the search result doesn't clear it instantly.
    const clear = () => setHighlight(null);
    const arm = window.setTimeout(
      () => document.addEventListener("pointerdown", clear, { once: true, capture: true }),
      400,
    );
    return () => {
      window.clearInterval(seek);
      window.clearTimeout(arm);
      document.removeEventListener("pointerdown", clear, true);
    };
  }, [highlight]);

  if (workspace.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading session…</p>;
  }
  if (workspace.isError || !workspace.data) {
    return (
      <p className="text-sm text-destructive">
        Couldn&apos;t load this session — it may have been deleted, or you haven&apos;t
        been invited to it.
      </p>
    );
  }
  const ws = workspace.data;

  const boardCount = (b: Board) =>
    ws.notes.filter((n) => n.board === b && n.status === "posted").length;

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <Link
          to="/planning"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> All sessions
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight">{ws.session.name}</h1>
          {ws.session.status === "archived" ? <Badge variant="secondary">Archived</Badge> : null}
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1.5 text-xs"
            onClick={() => setParticipantsOpen(true)}
          >
            <ColorDotStack
              items={ws.session.participants.map((p) => ({ color: p.color, title: p.display_name }))}
              max={6}
              size="md"
            />
            <Users className="h-3.5 w-3.5" />
            {ws.session.participants.length}
          </Button>
          <AIModelChip session={ws.session} />
          <SessionSearch
            workspace={ws}
            onNavigate={(t, hl) => {
              setTab(t);
              setHighlight(hl ?? null);
            }}
          />
        </div>
        {ws.session.description ? (
          <p className="text-sm text-muted-foreground">{ws.session.description}</p>
        ) : null}
      </header>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList variant="underline" className="flex-wrap">
          {[...SWOT_BOARDS, "impact_effort" as const].map((b) => {
            const meta = BOARD_META[b];
            const Icon = meta.icon;
            return (
              <TabsTrigger key={b} value={b} count={boardCount(b)}>
                <span className="inline-flex items-center gap-1.5">
                  <Icon className="h-3.5 w-3.5" />
                  {meta.label}
                </span>
              </TabsTrigger>
            );
          })}
          <TabsTrigger value="swot_summary">
            <span className="inline-flex items-center gap-1.5">
              <ClipboardList className="h-3.5 w-3.5" />
              SWOT Summary
            </span>
          </TabsTrigger>
          <TabsTrigger value="summary">
            <span className="inline-flex items-center gap-1.5">
              <Trophy className="h-3.5 w-3.5" />
              Summary
            </span>
          </TabsTrigger>
          <TabsTrigger value="how_to">
            <span className="inline-flex items-center gap-1.5">
              <CircleHelp className="h-3.5 w-3.5" />
              How to Use
            </span>
          </TabsTrigger>
        </TabsList>
        {SWOT_BOARDS.map((b) => (
          <TabsContent key={b} value={b} className="mt-4">
            <SwotBoardView workspace={ws} board={b} highlight={highlight} />
          </TabsContent>
        ))}
        <TabsContent value="impact_effort" className="mt-4">
          <ImpactEffortBoard workspace={ws} onNavigateBoard={setTab} highlight={highlight} />
        </TabsContent>
        <TabsContent value="swot_summary" className="mt-4">
          <SwotSummaryTab workspace={ws} onNavigateBoard={setTab} highlight={highlight} />
        </TabsContent>
        <TabsContent value="summary" className="mt-4">
          <SummaryTab workspace={ws} onNavigateBoard={setTab} />
        </TabsContent>
        <TabsContent value="how_to" className="mt-4">
          <HowToTab />
        </TabsContent>
      </Tabs>

      <ParticipantsDialog
        session={ws.session}
        open={participantsOpen}
        onOpenChange={setParticipantsOpen}
      />
    </div>
  );
}
